import {createStyles} from '@mantine/core';
import React, {useCallback} from 'react';

import {
  extractTableDataForSuggestions,
  GetTableDataFn,
} from 'notebook/ai-tools/suggest-plots/extractTableDataForSuggestions';
import SuggestedPlotsDropdown from 'notebook/ai-tools/suggest-plots/SuggestedPlotsDropdown';
import {arePlotSuggestionsSupportedSync} from 'notebook/ai-tools/suggest-plots/suggestPlotsUtils';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import APIUtil from 'util/APIUtil';

import {TableActionFunctionParameters} from '../StructuredTableConstants';
import makeCustomButtonActionFunction from './makeCustomButtonActionFunction';

interface SuggestedPlotsOnlyButtonProps {
  params: TableActionFunctionParameters<unknown, unknown>;
}

function SuggestedPlotsOnlyButton({params}: SuggestedPlotsOnlyButtonProps): JSX.Element | null {
  const {classes} = useStyles();

  const getTableData: GetTableDataFn = useCallback(() => {
    const table = params.structuredTableAdapter.toTable(params.structuredTable);
    return extractTableDataForSuggestions(table, params.computedTable);
  }, [params.structuredTableAdapter, params.structuredTable, params.computedTable]);

  if (!arePlotSuggestionsSupportedSync()) {
    return null;
  }

  const entryId = params.entryOrTemplateId;
  if (entryId == null) {
    return null;
  }
  if (!APIUtil.isEntry(entryId) && !APIUtil.isDocument(entryId)) {
    return null;
  }

  const disabled = params.locked || params.structuredTable.rows.length === 0;

  return (
    <SuggestedPlotsDropdown
      entryId={entryId}
      tableName={params.structuredTable.name}
      getTableData={getTableData}
      disabled={disabled}
      className={classes.button}
    />
  );
}

const useStyles = createStyles(() => ({
  button: {
    marginLeft: 6,
  },
}));

export const suggestedPlotsOnlyActionFunction = makeCustomButtonActionFunction({
  renderToolbarButton: (params: TableActionFunctionParameters<unknown, unknown>) => (
    <SuggestedPlotsOnlyButton params={params} />
  ),
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
  getHidden: (params: TableActionFunctionParameters<unknown, unknown>) =>
    APIUtil.isStageEntry(params.entryOrTemplateId ?? '') ||
    APIUtil.isStageEntryTemplate(params.entryOrTemplateId ?? ''),
});
